import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { stripMarkdown } from '../../components/MarkdownContent'
import {
  Upload,
  BookOpen,
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  ExternalLink,
  Video,
  FileText,
  Presentation,
  ClipboardList,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  EyeOff
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const CONTENT_TYPES = [
  { value: 'video', label: 'Video', icon: Video, color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  { value: 'document', label: 'Document', icon: FileText, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  { value: 'presentation', label: 'Presentation', icon: FileText, color: 'text-cinema-glow bg-cinema-glow/10 border-cinema-glow/20' },
  { value: 'quiz', label: 'Quiz', icon: HelpCircle, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
  { value: 'assignment', label: 'Assignment', icon: ClipboardList, color: 'text-green-400 bg-green-500/10 border-green-500/20' },
]

const getContentTypeInfo = (type) =>
  CONTENT_TYPES.find((t) => t.value === type) || CONTENT_TYPES[1]

const LESSONS_PER_PAGE = 8

const emptyForm = {
  title: '',
  description: '',
  content_type: 'video',
  content_url: '',
  duration_minutes: '',
  order_index: '',
  status: 'draft',
}

export default function UploadContent() {
  const [courses, setCourses] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [lessons, setLessons] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(true)
  const [loadingLessons, setLoadingLessons] = useState(false)

  // Form state
  const [showForm, setShowForm] = useState(false)
  const [editingLesson, setEditingLesson] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  // Expanded lesson
  const [expandedId, setExpandedId] = useState(null)
  const [materialsByLesson, setMaterialsByLesson] = useState({})
  const [materialForm, setMaterialForm] = useState({ title: '', materialType: 'link', url: '', isPublished: false })
  const [savingMaterial, setSavingMaterial] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    api.get('/trainers/courses')
      .then((res) => {
        setCourses(res.data)
        if (res.data.length === 1) setSelectedCourse(res.data[0])
      })
      .catch(() => toast.error('Failed to load courses'))
      .finally(() => setLoadingCourses(false))
  }, [])

  useEffect(() => {
    if (!selectedCourse) return
    setLoadingLessons(true)
    setCurrentPage(1)
    api.get(`/trainers/courses/${selectedCourse.id}/lessons`)
      .then((res) => setLessons(res.data))
      .catch(() => toast.error('Failed to load lessons'))
      .finally(() => setLoadingLessons(false))
  }, [selectedCourse])

  useEffect(() => {
    if (!selectedCourse || !expandedId) return
    api.get(`/trainers/courses/${selectedCourse.id}/lessons/${expandedId}/materials`)
      .then((res) => {
        setMaterialsByLesson((prev) => ({ ...prev, [expandedId]: res.data.materials || [] }))
      })
      .catch(() => {
        setMaterialsByLesson((prev) => ({ ...prev, [expandedId]: [] }))
      })
  }, [selectedCourse, expandedId])

  const openNewForm = () => {
    setEditingLesson(null)
    setForm({ ...emptyForm, order_index: lessons.length + 1 })
    setShowForm(true)
  }

  const openEditForm = (lesson) => {
    setEditingLesson(lesson)
    setForm({
      title: lesson.title || '',
      description: lesson.description || '',
      content_type: lesson.content_type || 'video',
      content_url: lesson.content_url || '',
      duration_minutes: lesson.duration_minutes || '',
      order_index: lesson.order_index || '',
      status: lesson.status || 'draft',
    })
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) { toast.error('Title is required'); return }
    setSaving(true)
    try {
      if (editingLesson) {
        const res = await api.put(`/trainers/lessons/${editingLesson.id}`, form)
        setLessons((prev) => prev.map((l) => l.id === editingLesson.id ? res.data.lesson : l))
        toast.success('Lesson updated')
      } else {
        const res = await api.post('/trainers/lessons', {
          ...form,
          course_id: selectedCourse.id,
        })
        setLessons((prev) => [...prev, res.data.lesson])
        toast.success('Lesson added')
      }
      setShowForm(false)
      setEditingLesson(null)
      setForm(emptyForm)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save lesson')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this lesson? This cannot be undone.')) return
    try {
      await api.delete(`/trainers/lessons/${id}`)
      setLessons((prev) => prev.filter((l) => l.id !== id))
      toast.success('Lesson deleted')
    } catch {
      toast.error('Failed to delete lesson')
    }
  }

  const togglePublish = async (lesson) => {
    const newStatus = lesson.status === 'published' ? 'draft' : 'published'
    try {
      const res = await api.put(`/trainers/lessons/${lesson.id}`, { ...lesson, status: newStatus })
      setLessons((prev) => prev.map((l) => l.id === lesson.id ? res.data.lesson : l))
      toast.success(newStatus === 'published' ? 'Lesson published' : 'Lesson set to draft')
    } catch {
      toast.error('Failed to update status')
    }
  }

  const addMaterial = async (lessonId) => {
    if (!selectedCourse) return
    if (!materialForm.title.trim() || !materialForm.url.trim()) {
      toast.error('Material title and URL are required')
      return
    }
    setSavingMaterial(true)
    try {
      const res = await api.post(`/trainers/courses/${selectedCourse.id}/lessons/${lessonId}/materials`, materialForm)
      setMaterialsByLesson((prev) => ({
        ...prev,
        [lessonId]: [res.data.material, ...(prev[lessonId] || [])],
      }))
      setMaterialForm({ title: '', materialType: 'link', url: '', isPublished: false })
      toast.success('Material added')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add material')
    } finally {
      setSavingMaterial(false)
    }
  }

  const toggleMaterialPublished = async (lessonId, material) => {
    if (!selectedCourse) return
    try {
      const res = await api.patch(
        `/trainers/courses/${selectedCourse.id}/lessons/${lessonId}/materials/${material.id}`,
        { isPublished: !material.isPublished }
      )
      setMaterialsByLesson((prev) => ({
        ...prev,
        [lessonId]: (prev[lessonId] || []).map((row) => row.id === material.id ? res.data.material : row),
      }))
    } catch {
      toast.error('Failed to update material')
    }
  }

  const deleteMaterial = async (lessonId, materialId) => {
    if (!selectedCourse) return
    if (!confirm('Remove this teaching material?')) return
    try {
      await api.delete(`/trainers/courses/${selectedCourse.id}/lessons/${lessonId}/materials/${materialId}`)
      setMaterialsByLesson((prev) => ({
        ...prev,
        [lessonId]: (prev[lessonId] || []).filter((row) => row.id !== materialId),
      }))
    } catch {
      toast.error('Failed to delete material')
    }
  }

  if (loadingCourses) return <LoadingSpinner label="Loading courses" />

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-3 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40 resize-none"

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="cinema-page-header flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light flex items-center gap-2">
            <Upload size={16} className="text-cinema-glow" /> Upload Content
          </h1>
        </div>
        {selectedCourse && (
          <button onClick={openNewForm} className="cinema-btn-primary text-xs px-4 py-2.5 min-h-0 flex items-center gap-2">
            <Plus size={13} /> Add Lesson
          </button>
        )}
      </div>

      {/* Course Selector */}
      <div>
        <label className="cinema-label flex items-center gap-1 mb-3"><BookOpen size={11} /> Select Course</label>
        {courses.length === 0 ? (
          <p className="font-sans text-sm text-cinema-muted">No courses assigned to you yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {courses.map((course) => (
              <button key={course.id} onClick={() => setSelectedCourse(course)}
                className={`text-left p-3.5 border transition-all ${
                  selectedCourse?.id === course.id
                    ? 'border-cinema-glow/30 bg-cinema-glow/8'
                    : 'border-white/8 hover:border-cinema-glow/20 hover:bg-white/3'
                }`}
              >
                <p className={`font-sans text-sm sm:text-base font-medium ${selectedCourse?.id === course.id ? 'text-cinema-glow' : 'text-white'}`}>{course.title}</p>
                <p className="font-sans text-xs text-cinema-muted mt-0.5 font-medium">{course.course_code}</p>
                <p className="font-sans text-xs text-cinema-muted/70 mt-1">{course.enrolled_students || 0} students</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lessons list */}
      {selectedCourse && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">
              Lessons — <span className="text-cinema-glow">{selectedCourse.title}</span>
            </h2>
            <span className="cinema-label">{lessons.length} lesson{lessons.length !== 1 ? 's' : ''}</span>
          </div>

          {loadingLessons ? <LoadingSpinner className="py-10" /> : lessons.length === 0 ? (
            <div className="cinema-empty">
              <Upload size={32} className="mx-auto mb-3 text-cinema-muted/20" />
              <p className="font-sans text-base text-cinema-muted font-medium">No lessons yet</p>
              <p className="font-sans text-xs text-cinema-muted/70 mt-1">Click "Add Lesson" to create your first lesson</p>
            </div>
          ) : (
            <>
              {lessons.slice((currentPage - 1) * LESSONS_PER_PAGE, currentPage * LESSONS_PER_PAGE).map((lesson, idx) => {
                const globalIdx = (currentPage - 1) * LESSONS_PER_PAGE + idx
                const typeInfo = getContentTypeInfo(lesson.content_type)
                const TypeIcon = typeInfo.icon
                const isExpanded = expandedId === lesson.id
                const isPublished = lesson.status === 'published'

                return (
                  <div key={lesson.id} className={`border-l-2 ${isPublished ? 'border-l-green-500/50' : 'border-l-white/10'} cinema-list-row flex-col !items-stretch py-4`}>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 border border-white/10 flex items-center justify-center font-sans text-xs text-cinema-muted shrink-0 mt-0.5">
                        {lesson.order_index || globalIdx + 1}
                      </span>
                      <span className={`p-1.5 border shrink-0 ${typeInfo.color}`}>
                        <TypeIcon size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <button onClick={() => setExpandedId(isExpanded ? null : lesson.id)}
                              className="text-left font-sans text-sm sm:text-base text-white hover:text-cinema-glow flex items-center gap-1.5 transition-colors font-medium">
                              {lesson.title}
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className={`font-sans text-xs font-medium px-2 py-0.5 border ${typeInfo.color}`}>{typeInfo.label}</span>
                              <span className={`font-sans text-xs font-medium px-2 py-0.5 border ${isPublished ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-white/5 text-cinema-muted border-white/10'}`}>
                                {isPublished ? 'Published' : 'Draft'}
                              </span>
                              {lesson.duration_minutes && (
                                <span className="font-sans text-xs text-cinema-muted flex items-center gap-1">
                                  <Clock size={11} /> {lesson.duration_minutes}m
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => togglePublish(lesson)}
                              className={`p-1.5 transition-colors ${isPublished ? 'text-green-400 hover:bg-green-500/10' : 'text-cinema-muted hover:bg-white/5'}`}
                              title={isPublished ? 'Unpublish' : 'Publish'}>
                              {isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            <button onClick={() => openEditForm(lesson)} className="p-1.5 text-blue-400 hover:bg-blue-500/10 transition-colors" title="Edit">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => handleDelete(lesson.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 transition-colors" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-white/6 space-y-2">
                            {lesson.description && <p className="font-sans text-sm text-cinema-muted">{stripMarkdown(lesson.description)}</p>}
                            {lesson.content_url ? (
                              <a href={lesson.content_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 font-sans text-sm text-cinema-glow hover:underline">
                                <ExternalLink size={13} /> Open Content Link
                              </a>
                            ) : (
                              <p className="font-sans text-sm text-cinema-muted/50 italic">No content URL provided</p>
                            )}
                            <div className="pt-2 space-y-2">
                              <p className="font-sans text-xs uppercase tracking-wider text-cinema-muted font-medium">Teaching materials</p>
                              {(materialsByLesson[lesson.id] || []).map((material) => (
                                <div key={material.id} className="flex items-center gap-2 text-xs sm:text-sm">
                                  <a href={material.url} target="_blank" rel="noopener noreferrer" className="text-cinema-glow hover:underline truncate">
                                    {material.title}
                                  </a>
                                  <span className="text-cinema-muted">{material.isPublished ? 'Published' : 'Unpublished'}</span>
                                  <button type="button" onClick={() => toggleMaterialPublished(lesson.id, material)} className="text-cinema-muted hover:text-white">
                                    {material.isPublished ? 'Unpublish' : 'Publish'}
                                  </button>
                                  <button type="button" onClick={() => deleteMaterial(lesson.id, material.id)} className="text-red-400 hover:text-red-300">
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <div className="grid gap-2 sm:grid-cols-2">
                                <input
                                  value={materialForm.title}
                                  onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                                  placeholder="Material title"
                                  className={inputCls}
                                />
                                <input
                                  value={materialForm.url}
                                  onChange={(e) => setMaterialForm({ ...materialForm, url: e.target.value })}
                                  placeholder="https://"
                                  className={inputCls}
                                />
                                <select
                                  value={materialForm.materialType}
                                  onChange={(e) => setMaterialForm({ ...materialForm, materialType: e.target.value })}
                                  className={inputCls}
                                >
                                  <option value="link">Link</option>
                                  <option value="document">Document</option>
                                  <option value="video">Video</option>
                                  <option value="file">File</option>
                                </select>
                                <label className="flex items-center gap-2 font-sans text-xs text-cinema-muted">
                                  <input
                                    type="checkbox"
                                    checked={materialForm.isPublished}
                                    onChange={(e) => setMaterialForm({ ...materialForm, isPublished: e.target.checked })}
                                  />
                                  Publish for students
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={() => addMaterial(lesson.id)}
                                disabled={savingMaterial}
                                className="cinema-btn-primary text-xs px-3 py-2 min-h-0"
                              >
                                Add material
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
              {Math.ceil(lessons.length / LESSONS_PER_PAGE) > 1 && (
                <div className="flex items-center justify-center gap-3 mt-2">
                  <button onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                    disabled={currentPage === 1}
                    className="p-2 border border-white/10 text-cinema-muted hover:text-white hover:border-cinema-glow/30 disabled:opacity-30 transition-all">
                    <ChevronUp size={13} className="rotate-[-90deg]" />
                  </button>
                  <span className="font-sans text-xs text-cinema-muted tracking-widest uppercase">{currentPage} / {Math.ceil(lessons.length / LESSONS_PER_PAGE)}</span>
                  <button onClick={() => { setCurrentPage(p => Math.min(Math.ceil(lessons.length / LESSONS_PER_PAGE), p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                    disabled={currentPage === Math.ceil(lessons.length / LESSONS_PER_PAGE)}
                    className="p-2 border border-white/10 text-cinema-muted hover:text-white hover:border-cinema-glow/30 disabled:opacity-30 transition-all">
                    <ChevronDown size={13} className="rotate-[-90deg]" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-base text-white font-light">{editingLesson ? 'Edit Lesson' : 'Add New Lesson'}</h3>
              <button onClick={() => { setShowForm(false); setEditingLesson(null) }} className="text-cinema-muted hover:text-white transition-colors"><X size={16} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="cinema-label block mb-1.5">Title <span className="text-red-400">*</span></label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={inputCls} placeholder="e.g. Introduction to Photography" required />
              </div>
              <div>
                <label className="cinema-label block mb-1.5">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={inputCls} placeholder="Brief description of this lesson..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="cinema-label block mb-1.5">Content Type</label>
                  <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className={inputCls}>
                    {CONTENT_TYPES.map((t) => <option key={t.value} value={t.value} className="bg-gray-900">{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="cinema-label block mb-1.5">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    <option value="draft" className="bg-gray-900">Draft</option>
                    <option value="published" className="bg-gray-900">Published</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="cinema-label block mb-1.5">Content URL</label>
                <input type="url" value={form.content_url} onChange={(e) => setForm({ ...form, content_url: e.target.value })}
                  className={inputCls} placeholder="https://drive.google.com/... or YouTube link" />
                <p className="font-sans text-xs text-cinema-muted/70 mt-1">Paste a link to Google Drive, YouTube, Dropbox, etc.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="cinema-label block mb-1.5">Duration (minutes)</label>
                  <input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                    className={inputCls} placeholder="e.g. 45" />
                </div>
                <div>
                  <label className="cinema-label block mb-1.5">Order</label>
                  <input type="number" min="1" value={form.order_index} onChange={(e) => setForm({ ...form, order_index: e.target.value })}
                    className={inputCls} placeholder="e.g. 1" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 flex items-center gap-2 flex-1 justify-center disabled:opacity-50">
                  <Save size={13} /> {saving ? 'Saving...' : (editingLesson ? 'Update Lesson' : 'Add Lesson')}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingLesson(null) }} className="cinema-btn-ghost text-xs px-4 py-2.5 min-h-0">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
